import { useEffect, useState } from "react";
import { Reveal, MaskLineInView } from "./Reveal";
import { api, shareContent } from "@/lib/apiClient";
import CashAppLine from "./CashAppLine";
import { toast } from "sonner";
import { PlayCircle, CalendarDays, Share2 } from "lucide-react";

export const ytId = (url) => {
  const m = url?.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{11})/);
  return m ? m[1] : null;
};

const ORDINALS = ["First", "Second", "Third", "Fourth"];

function MessageSlot({ message, index }) {
  if (!message) {
    return (
      <div className="glass rounded-3xl p-4 flex flex-col" data-testid={`message-slot-empty-${index}`}>
        <div className="relative flex-1 min-h-[220px] w-full rounded-2xl border border-dashed border-white/15 bg-white/[0.02]">
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-[#7A708A]">
            <CalendarDays className="w-8 h-8" strokeWidth={1.2} />
            <p className="font-mono text-[0.65rem] uppercase tracking-[0.25em]">{ORDINALS[index]} Sunday</p>
            <p className="text-sm font-light">Message coming soon</p>
          </div>
        </div>
      </div>
    );
  }

  const vid = ytId(message.youtube_url);
  const share = async () => {
    const result = await shareContent({
      title: `${message.title} — F.O.L.W.A.C.`,
      text: `Watch "${message.title}" from Fountain of Living Water Apostolic Church.`,
      url: message.youtube_url,
    });
    if (result === "copied") toast.success("Link copied — share it anywhere.");
  };
  return (
    <div className="glass rounded-3xl p-4 flex flex-col" data-testid={`message-slot-${index}`}>
      <div className="relative w-full overflow-hidden rounded-2xl" style={{ paddingBottom: "56.25%" }}>
        {vid ? (
          <iframe
            src={`https://www.youtube.com/embed/${vid}`}
            title={message.title}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <a href={message.youtube_url} target="_blank" rel="noreferrer" className="absolute inset-0 flex items-center justify-center text-gold gap-2">
            <PlayCircle className="w-6 h-6" /> Watch the message
          </a>
        )}
      </div>
      <div className="px-2 pt-4 pb-1 flex-1 flex flex-col">
        <p className="font-mono text-[0.6rem] uppercase tracking-[0.25em] text-gold">{ORDINALS[index]} Sunday</p>
        <p className="text-white font-display text-lg leading-snug mt-1.5">{message.title}</p>
        {message.note && <p className="text-xs text-[#B4ADC1] font-light mt-1">{message.note}</p>}
        <p className="text-[0.6rem] font-mono text-[#7A708A] mt-auto pt-3 flex items-center justify-between">
          {new Date(message.created_at).toLocaleDateString()}
          <button
            onClick={share}
            className="flex items-center gap-1.5 text-[#B4ADC1] hover:text-white transition-colors text-[0.65rem] font-mono uppercase tracking-wider"
            data-testid={`message-share-${index}`}
          >
            <Share2 className="w-3.5 h-3.5" /> Share
          </button>
        </p>
      </div>
    </div>
  );
}

export default function MonthlyMessages() {
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    api.get("/messages").then(({ data }) => setMessages(data)).catch(() => {});
  }, []);

  const slots = [0, 1, 2, 3].map((i) => messages[i] || null);

  return (
    <section id="message" className="relative py-28 md:py-40 bg-[#05030A]">
      <div className="absolute right-0 top-24 w-[480px] h-[380px] rounded-full bg-[#00E5FF]/8 blur-[140px]" />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <Reveal><span className="section-label">The Word — Monthly Messages</span></Reveal>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.02] mt-5 text-white">
            <MaskLineInView>A living word for</MaskLineInView>
            <MaskLineInView delay={0.1}><span className="italic text-gold">every Sunday.</span></MaskLineInView>
          </h2>
          <Reveal delay={0.2}>
            <p className="text-lg text-[#B4ADC1] font-light mt-6 leading-relaxed">
              The four most recent Sunday messages from Fountain of Living Water — one for each week of the month.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="grid sm:grid-cols-2 gap-5 mt-14" data-testid="monthly-messages-grid">
            {slots.map((m, i) => (
              <MessageSlot key={m ? m.id : `empty-${i}`} message={m} index={i} />
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="text-center mt-14">
            <CashAppLine testId="cashapp-middle" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}
